// ABOUTME: Watchdog for the HTML5 intro film on grid tenants — ends the intro if the <video>
// ABOUTME: never fires `ended`/`error` (stalled load), so the mic/VAD gate cannot stay shut.
//
// Manual test case (grid tenant, e.g. TENANT_PROFILE=watches): in DevTools → Network, block the
// intro .mp4 request, click "Enter Boutique". Expected: black film screen, then after 45 s the
// grid appears and the concierge speaks the welcome; the mic pill reacts to your voice. Unblock
// and reload: the film plays to its end and the grid appears at once (no early cut).
import { useEffect } from 'react';

/** Grace added to the film's own duration before the watchdog gives up on it. */
export const INTRO_WATCHDOG_GRACE_MS = 6000;
/** Ceiling used until `loadedmetadata` tells us the real duration (a film that never loads). */
export const INTRO_WATCHDOG_NO_METADATA_MS = 45000;

/**
 * While `active`, arm a timer for the film's duration + grace (re-armed when metadata arrives)
 * and call `onExpire` once if the film is still showing when it fires. The Projectionist has its
 * own stall watchdog for the reel; the grid's plain <video> had none, so a film that stalled
 * left `showIntroRef` true for the whole session: VAD silent, every mic frame dropped.
 */
export function useIntroWatchdog({ videoRef, active, onExpire }) {
  useEffect(() => {
    if (!active) return undefined;
    let timer = null;
    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      onExpire && onExpire();
    };
    const arm = (ms) => {
      clearTimeout(timer);
      timer = setTimeout(fire, ms);
    };
    arm(INTRO_WATCHDOG_NO_METADATA_MS);
    const video = videoRef && videoRef.current;
    const onMeta = () => {
      const d = video && Number.isFinite(video.duration) ? video.duration : 0;
      if (d > 0) arm(d * 1000 + INTRO_WATCHDOG_GRACE_MS);
    };
    if (video) {
      if (Number.isFinite(video.duration) && video.duration > 0) onMeta();
      video.addEventListener('loadedmetadata', onMeta);
    }
    return () => {
      clearTimeout(timer);
      if (video) video.removeEventListener('loadedmetadata', onMeta);
    };
  }, [videoRef, active, onExpire]);
}
